<script lang="ts" setup>
import { Role, InvitedUserInterface } from "../../../interface/UserInterface";
import ProfileIconComponent from "../../../components/ProfileIconComponent.vue";

const props = defineProps({
  user: {
    type: Object as () => InvitedUserInterface,
    required: true,
  },
});
</script>

<template>
  <div
    class="user-card d-flex border-bottom p-md-3 py-2 align-items-center no-pointer"
  >
    <ProfileIconComponent :user="props.user" />
    <div class="d-flex flex-column justify-content-center">
      <span class="fw-semibold name text-capitalize">
        {{ props.user.invited_user.first_name }}
        {{ props.user.invited_user.last_name }}
      </span>

      <a
        :href="`mailto:${props.user?.invited_user?.email}`"
        class="email break-word cursor-pointer text-main-mail"
        target="_blank"
        rel="noopener noreferrer"
        @click.stop
      >
        {{ props.user.invited_user.email }}
      </a>

      <div class="role-wrapper d-flex align-items-center mt-1">
        <span
          class="invite-tag"
          :class="props.user.invitation_sent ? 'text-secondary' : 'text-danger'"
        >
          {{ props.user.invitation_sent ? "Invited" : "Not Invited" }}
        </span>

        <span class="pipe">|</span>
        <span class="text-capitalize">{{ props.user.invitation_type }}</span>

        <!-- Participant specific info -->
        <template v-if="props.user.invitation_type === Role.Participants">
          <span class="pipe">|</span>
          <span>Invited Raters: {{ props.user.item_count }}</span>
          <template v-if="props.user.invitation_sent">
            <span class="pipe">|</span>
            <span>
              Days Left:
              {{
                props.user.days_left && props.user.days_left > 0
                  ? props.user.days_left
                  : 0
              }}
            </span>
          </template>
          <span class="pipe">|</span>
          <span class="break-word">
            Admin:
            <a
              :href="`mailto:${props.user.invited_for_user?.email}`"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ props.user.invited_for_user?.email }}
            </a>
          </span>
        </template>

        <!-- Rater specific info -->
        <template v-if="props.user.invitation_type === Role.Raters">
          <span class="pipe">|</span>
          <span class="break-word">
            Participant:
            <a
              :href="`mailto:${props.user.invited_for_user?.email}`"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ props.user.invited_for_user?.email }}
            </a>
          </span>
        </template>
      </div>

      <!-- Company badge on its own line -->
      <template v-if="props.user.company?.name">
        <div class="mt-1">
          <router-link
            :to="`/company/${props.user.company.id}/manage-employees`"
            class="company-badge"
            @click.stop
          >
            <i class="bi bi-buildings"></i>
            {{ props.user.company.name }}
          </router-link>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../theme/variables";

.user-card {
  transition: all ease 300ms;
  gap: 12px;
}

.email {
  font-size: 12px;
  color: #6e6e6e;
}

.invite-tag {
  font-weight: 500;
  font-size: 11px !important;
}

.role-wrapper {
  gap: 10px;
  span {
    font-size: 12px;
  }
}

.no-pointer {
  cursor: default;
}

.text-main-mail:hover {
  color: #0066cc;
  text-decoration: underline;
  opacity: 0.8;
}

.company-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: var(--bs-primary);
  text-decoration: none;
  background-color: rgba(var(--bs-primary-rgb), 0.08);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.25);
  border-radius: 20px;
  padding: 2px 8px;

  i {
    font-size: 12px;
  }

  &:hover {
    background-color: rgba(var(--bs-primary-rgb), 0.18);
    text-decoration: none;
  }
}

@media (max-width: 575px) {
  .role-wrapper {
    flex-direction: column;
    align-items: start !important;
    gap: 0;
  }
  .pipe {
    display: none;
  }
}
</style>
